import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import IosShareIcon from '@mui/icons-material/IosShare';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { clickNative, loadPdfJs, onOpenPdf, openOutside } from './pdfViewer';
import { serverFileName } from '../fileDownload';

const MAX_PAGES = 60;

const nameOf = (src) => {
  if (src.name) return src.name;
  try {
    const p = new URL(src.url, window.location.href).pathname;
    const last = decodeURIComponent(p.split('/').filter(Boolean).pop() || '');
    return /\.pdf$/i.test(last) ? last : 'document.pdf';
  } catch (e) {
    return 'document.pdf';
  }
};

/* The PDF viewer of the installed app (see pdfViewer.js): full screen, «Назад»
   in the corner (and the phone's back gesture), pages drawn by pdf.js, zoom,
   «Поделиться» → the system sheet (save to Files, print, send). */
export default function PdfViewerHost() {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState({ loading: false, error: '', pages: 0 });
  const [zoom, setZoom] = useState(1);
  const [serverName, setServerName] = useState('');
  const boxRef = useRef(null);
  const blobRef = useRef(null);
  const docRef = useRef(null);

  useEffect(
    () =>
      onOpenPdf((detail) => {
        blobRef.current = detail.blob || null;
        setServerName('');
        docRef.current = null;
        setZoom(1);
        setSrc(detail);
      }),
    []
  );

  // the phone's «back» closes the viewer, not the page under it
  useEffect(() => {
    if (!src) return undefined;
    window.history.pushState(
      { ...(window.history.state || {}), cpPdf: true },
      ''
    );
    const onPop = () => setSrc(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [src]);

  const close = () => {
    if (window.history.state && window.history.state.cpPdf)
      window.history.back();
    else setSrc(null);
  };

  const getBlob = useCallback(async () => {
    if (blobRef.current) return blobRef.current;
    const res = await fetch(src.url, { credentials: 'include' });
    if (!res.ok) throw new Error(`Файл не открылся (ошибка ${res.status})`);
    setServerName(serverFileName(res, ''));
    blobRef.current = await res.blob();
    return blobRef.current;
  }, [src]);

  // load once per file
  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    setState({ loading: true, error: '', pages: 0 });
    (async () => {
      const [lib, blob] = await Promise.all([loadPdfJs(), getBlob()]);
      const pdf = await lib.getDocument({ data: await blob.arrayBuffer() })
        .promise;
      if (cancelled) return;
      docRef.current = pdf;
      setState({ loading: false, error: '', pages: pdf.numPages });
    })().catch((e) => {
      if (!cancelled)
        setState({ loading: false, error: e.message || 'Ошибка', pages: 0 });
    });
    return () => {
      cancelled = true;
    };
  }, [src, getBlob]);

  // draw the pages (again on zoom)
  useEffect(() => {
    const pdf = docRef.current;
    const box = boxRef.current;
    if (!pdf || !box || !state.pages) return undefined;
    let cancelled = false;
    box.innerHTML = '';
    const width = Math.max(200, box.clientWidth - 16) * zoom;
    const dpr = Math.min(window.devicePixelRatio || 1, zoom > 1 ? 1.5 : 2);
    (async () => {
      for (let i = 1; i <= Math.min(pdf.numPages, MAX_PAGES); i += 1) {
        if (cancelled) return;
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const vp = page.getViewport({ scale: (width / base.width) * dpr });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        canvas.style.width = `${Math.floor(vp.width / dpr)}px`;
        canvas.style.height = `${Math.floor(vp.height / dpr)}px`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto 10px';
        canvas.style.background = '#fff';
        canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,.25)';
        box.appendChild(canvas);
        // eslint-disable-next-line no-await-in-loop
        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: vp,
        }).promise;
      }
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state.pages, zoom]);

  const share = async () => {
    try {
      const blob = await getBlob();
      const file = new File([blob], src.name || serverName || nameOf(src), {
        type: 'application/pdf',
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: file.name });
        return;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(file);
      a.download = file.name;
      clickNative(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) {
      if (e && e.name !== 'AbortError')
        setState((s) => ({ ...s, error: e.message }));
    }
  };

  if (!src) return null;
  const title = (src.name || serverName || nameOf(src)).replace(/\.pdf$/i, '');
  return (
    <Dialog open fullScreen onClose={close} sx={{ zIndex: 2000 }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        sx={{ pt: 'env(safe-area-inset-top)' }}
      >
        <Toolbar sx={{ gap: 0.5, minHeight: 52 }}>
          <Button
            onClick={close}
            startIcon={<ArrowBackIosNewIcon />}
            sx={{ fontWeight: 700, flexShrink: 0 }}
          >
            Назад
          </Button>
          <Typography
            noWrap
            sx={{ flex: 1, fontWeight: 700, fontSize: '0.92rem', mx: 0.5 }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="Мельче"
            onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
            disabled={zoom <= 1}
          >
            <ZoomOutIcon />
          </IconButton>
          <IconButton
            aria-label="Крупнее"
            onClick={() => setZoom((z) => Math.min(2.5, z + 0.5))}
            disabled={zoom >= 2.5}
          >
            <ZoomInIcon />
          </IconButton>
          <IconButton aria-label="Поделиться или сохранить" onClick={share}>
            <IosShareIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          bgcolor: '#e9e7ec',
          py: 1.5,
          pb: 'calc(12px + env(safe-area-inset-bottom))',
        }}
      >
        {state.loading && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {state.error && (
          <Box sx={{ p: 2, display: 'grid', gap: 1.5 }}>
            <Alert severity="error">{state.error}</Alert>
            {src.url && (
              <Button variant="outlined" onClick={() => openOutside(src.url)}>
                Открыть в браузере
              </Button>
            )}
          </Box>
        )}
        {state.pages > MAX_PAGES && (
          <Alert severity="info" sx={{ mx: 1.5, mb: 1 }}>
            Показаны первые {MAX_PAGES} страниц из {state.pages} — весь файл
            откроется через «Поделиться».
          </Alert>
        )}
        <Box
          ref={boxRef}
          sx={{ px: 1, overflowX: zoom > 1 ? 'auto' : 'hidden' }}
        />
      </Box>
    </Dialog>
  );
}
